import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';

import { Form, Spinner } from 'components';
import { FormWrapper } from 'components/Modal/styles';
import * as Roles from 'constants/roles';
import { useQueryClient } from '@tanstack/react-query';
import { useRegionSwitcherStore } from 'stores/regionSwitcherStore';
import { useSelectedProfileStore } from 'stores/selectedProfileStore';
import { currencies } from 'constants/currenciesList';
import ModalForm from 'components/ModalForm/ModalForm';
import { useFormikContext } from 'formik';
import { ErrorFeedback } from 'components/Form/styles';
import { mapInitialValues } from 'utils/regionFormUtils';
import {
  useCheckRegionAvailability,
  useCheckSubdomainAvailability,
  useCreateRegion,
} from 'api/regions/hooks';
import { useAuthStore } from 'stores/authStore';
import { DASHBOARD } from 'constants/routes';
import { useNavigate } from 'react-router';
import validationSchemas from './schemas';

const RegionForm = ({ FormGroup }) => {
  const [debouncedRegionName, setDebouncedRegionName] = useState(null);
  const [debouncedSubdomain, setDebouncedSubdomain] = useState(null);

  const { t } = useTranslation(['pages', 'app']);
  const navigate = useNavigate();
  const role = useAuthStore((state) => state.role);
  const brand = useAuthStore((state) => state.user.admin_details.brand);
  const queryClient = useQueryClient();
  const { mutateAsync: createRegionAsync, isPending: isCreating } = useCreateRegion();
  const setSelectedProfile = useSelectedProfileStore((state) => state.setSelectedProfile);
  const closeRegionSwitcher = useRegionSwitcherStore((state) => state.closeRegionSwitcher);

  const { data: regionAvailabilityData, isFetching: isRegionLoading } =
    useCheckRegionAvailability(debouncedRegionName);
  const { data: subdomainAvailabilityData, isFetching: isDomainLoading } =
    useCheckSubdomainAvailability(debouncedSubdomain);

  const isRegionAvailable = regionAvailabilityData?.available ?? true;
  const isDomainAvailable = subdomainAvailabilityData?.available ?? true;
  const regionAvailabilityMessage =
    regionAvailabilityData?.message || t('pages:regions.nameNotAvailable');
  const domainAvailabilityMessage =
    subdomainAvailabilityData?.message || t('pages:regions.subdomainNotAvailable');

  const isSuperAdmin = role === Roles.ADMIN;

  const domainTaken = 'fe-x-circle text-danger';
  const domainAvailable = 'fe-check-circle text-success';

  const debouncedSetRegionName = useMemo(
    () =>
      debounce((name) => {
        setDebouncedRegionName(name);
      }, 500),
    [],
  );

  const debouncedSetSubdomain = useMemo(
    () =>
      debounce((subdomain) => {
        setDebouncedSubdomain(subdomain);
      }, 500),
    [],
  );

  const RegionAvailabiltyCheck = () => {
    const { values, validateField } = useFormikContext();

    useEffect(() => {
      if (values?.name?.length && isSuperAdmin) {
        debouncedSetRegionName(values.name);
      }

      return () => {
        debouncedSetRegionName.cancel();
      };
    }, [values.name]);

    useEffect(() => {
      validateField('name');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRegionAvailable, validateField]);

    return null;
  };

  const DomainAvailabilityCheck = () => {
    const { values, validateField } = useFormikContext();

    useEffect(() => {
      if (values?.subdomain?.length && isSuperAdmin) {
        debouncedSetSubdomain(values.subdomain);
      }

      return () => {
        debouncedSetSubdomain.cancel();
      };
    }, [values.subdomain]);

    useEffect(() => {
      validateField('subdomain');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDomainAvailable, validateField]);

    return null;
  };

  const initialValues = useMemo(() => mapInitialValues({}, brand?.uid), [brand]);

  const handleSubmit = async ({ name, subdomain, base_currency }) => {
    const createdRegion = await createRegionAsync({
      name,
      subdomain,
      base_currency,
      brand_uid: brand?.uid,
    });

    queryClient.resetQueries();
    if (createdRegion?.uid) {
      setSelectedProfile({ brandUid: brand.uid, regionUid: createdRegion.uid });
    }
    closeRegionSwitcher();
    navigate(DASHBOARD);
  };
  return (
    <ModalForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchemas.region}
      cancelLabel={t('app:cancel')}
      submitLabel={t('app:create')}
      isSubmitting={isCreating}
    >
      {({ errors, touched }) => (
        <FormWrapper type="create">
          <FormGroup required label={t('pages:regions.name')}>
            <Form.Input
              type="text"
              name="name"
              disabled={!isSuperAdmin}
              validate={() => (isRegionAvailable ? undefined : regionAvailabilityMessage)}
              showErrorFeedback={false}
              prepend={
                isRegionLoading ? (
                  <Spinner style={{ width: '17px', height: '17px' }} />
                ) : isRegionAvailable && !errors?.name && isSuperAdmin ? (
                  <i className={`fe ${domainAvailable}`} />
                ) : (
                  <i className={`fe ${domainTaken}`} />
                )
              }
            />
            <RegionAvailabiltyCheck />
            {((touched.name && errors.name) || errors.name === regionAvailabilityMessage) && (
              <ErrorFeedback>{errors.name}</ErrorFeedback>
            )}
          </FormGroup>
          <FormGroup required label={t('pages:regions.subdomain')}>
            <Form.Input
              type="text"
              name="subdomain"
              disabled={!isSuperAdmin}
              validate={() => (isDomainAvailable ? undefined : domainAvailabilityMessage)}
              showErrorFeedback={false}
              placeholder="custom-subdomain"
              append=".ramblas.io"
              prepend={
                isDomainLoading ? (
                  <Spinner style={{ width: '17px', height: '17px' }} />
                ) : isDomainAvailable && !errors?.subdomain && isSuperAdmin ? (
                  <i className={`fe ${domainAvailable}`} />
                ) : (
                  <i className={`fe ${domainTaken}`} />
                )
              }
            />
            <DomainAvailabilityCheck />
            {((touched.subdomain && errors.subdomain) ||
              errors.subdomain === domainAvailabilityMessage) && (
              <ErrorFeedback>{errors.subdomain}</ErrorFeedback>
            )}
          </FormGroup>
          <FormGroup required label={t('pages:regions.currency')}>
            <Form.MultiSelect
              options={currencies.map((currency) => ({
                value: currency.code,
                label: `${currency.code} - ${currency.description}`,
              }))}
              name="base_currency"
              isMulti={false}
              disabled={!isSuperAdmin}
            />
          </FormGroup>
        </FormWrapper>
      )}
    </ModalForm>
  );
};

RegionForm.propTypes = {
  FormGroup: PropTypes.func,
};

export default RegionForm;
